import {
  isUsdFileType,
  isValid2DFileType,
  isValid3DFileType,
  isValidScanFileType,
  isValidTextFileType,
  VALID_2D_EXTENSIONS,
  VALID_3D_EXTENSIONS,
  VALID_SCAN_EXTENSIONS,
  VALID_TEXT_EXTENSIONS,
  VALID_USD_EXTENSIONS,
} from '@physna/supported-files';

/**
 * USD and scans are admitted by the shared supported-files lists, so every caller would otherwise
 * offer them to every tenant while the server rejects the upload. Omitting an option excludes that
 * type, matching the server's default-off `usd_ingest` and `scene_ingestion` gates — read the flags
 * via `useSupportedFileTypes`.
 */
export interface FileTypeOptions {
  includeUsd?: boolean;
  includeScans?: boolean;
}

export const INVALID_CHARS = /[\\/:*?"<>|\n\r\t]/;

export const MAX_FILE_SIZE = 1024 * 1024 * 1024; // 1GB in bytes
export const UPLOAD_FILES_MAX_QUEUE_SIZE = 3000;

export const ERROR_MESSAGES = {
  INVALID_FILE_TYPE: 'Invalid File Type',
  INVALID_FILE_SIZE: 'Invalid File Size',
  INVALID_FILENAME: 'Invalid Filename',
  UPLOAD_FAILED: 'Upload failed',
} as const;

const isGatedOut = (extension: string, options?: FileTypeOptions): boolean =>
  (!options?.includeUsd && VALID_USD_EXTENSIONS.includes(extension)) ||
  (!options?.includeScans && VALID_SCAN_EXTENSIONS.includes(extension));

const admittedExtensions = (extensions: readonly string[], options?: FileTypeOptions): string[] =>
  extensions.filter(extension => !isGatedOut(extension, options)).sort((a, b) => a.localeCompare(b));

/** The model formats alone, for a surface that filters models rather than every uploadable file. */
export const getModelFileExtensions = (options?: FileTypeOptions): string[] =>
  admittedExtensions(VALID_3D_EXTENSIONS, options);

export const getSupportedFileExtensions = (options?: FileTypeOptions): string =>
  admittedExtensions(
    [...VALID_3D_EXTENSIONS, ...VALID_SCAN_EXTENSIONS, ...VALID_2D_EXTENSIONS, ...VALID_TEXT_EXTENSIONS],
    options
  ).join(', ');

export const getSupportedFileTypesMessage = (options?: FileTypeOptions): string =>
  `Supported file types: ${getSupportedFileExtensions(options)}`;

export const isValidFilename = (filename: string): boolean => {
  if (!filename) return false;
  return !INVALID_CHARS.test(filename);
};

export function isValidFileType(filename: string, options?: FileTypeOptions): boolean {
  if (isUsdFileType(filename)) {
    return Boolean(options?.includeUsd);
  }
  if (isValidScanFileType(filename)) {
    return Boolean(options?.includeScans);
  }
  return isValid3DFileType(filename) || isValid2DFileType(filename) || isValidTextFileType(filename);
}

export const validateFileSize = (file: File): boolean => {
  return file.size > 0 && file.size <= MAX_FILE_SIZE;
};

export const validateFileName = (file: File): boolean => {
  return isValidFilename(file.name);
};

export const getFileValidationError = (file: File, options?: FileTypeOptions): string => {
  if (!isValidFileType(file.name, options)) {
    return ERROR_MESSAGES.INVALID_FILE_TYPE;
  }

  if (!validateFileSize(file)) {
    return ERROR_MESSAGES.INVALID_FILE_SIZE;
  }

  if (!validateFileName(file)) {
    return ERROR_MESSAGES.INVALID_FILENAME;
  }

  return '';
};

export const isInvalidFileTypeError = (message: string | undefined): boolean => {
  return message === ERROR_MESSAGES.INVALID_FILE_TYPE;
};

export const isValidFile = (file: File, options?: FileTypeOptions): boolean =>
  isValidFileType(file.name, options) && validateFileSize(file) && validateFileName(file);
